import React, { useEffect, useState } from 'react';
import { DepartmentResponse, Department } from '../../interfaces/department.interface';
import { DepartmentService } from '../../services/department.service';
import { Box, Button, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EditIcon from '@mui/icons-material/Edit';
import GroupIcon from '@mui/icons-material/Group';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { UserType } from '../../interfaces/userType.enum';

const DepartmentList: React.FC = () => {
    const [departmentData, setDepartmentData] = useState<DepartmentResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const toast = useToast();
    const { t } = useTranslation(['department', 'common']);
    const { user } = useAuth();
    const userRoles = user && user.roles ? (Array.isArray(user.roles) ? user.roles : [user.roles]) : [];
    const isInstitutionAdmin = userRoles.includes(UserType.InstitutionAdmin);

    useEffect(() => {
        fetchDepartments();
    }, []);

    const fetchDepartments = async () => {
        try {
            const data = await DepartmentService.getDepartmentsByInstitution();
            setDepartmentData(data);
        } catch (err) {
            toast.showApiError(err, t('departmentList.fetchFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (department: Department) => {
        navigate(`/departments/edit/${department.id}`, { state: { department } });
    };

    if (loading) return <Typography>{t('departmentList.loading')}</Typography>;
    if (!departmentData) return <Typography>{t('departmentList.empty')}</Typography>;

    return (
        <Box sx={{ padding: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">{t('departmentList.title')}</Typography>
                {isInstitutionAdmin && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => navigate('/departments/create')}
                    >
                        {t('departmentList.add')}
                    </Button>
                )}
            </Box>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('common:fields.name')}</TableCell>
                            <TableCell>{t('common:fields.description')}</TableCell>
                            <TableCell>{t('common:fields.actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {departmentData.content.map((department) => (
                            <TableRow key={department.id}>
                                <TableCell>{department.name}</TableCell>
                                <TableCell>{department.description || '-'}</TableCell>
                                <TableCell>
                                    <IconButton onClick={() => navigate(`/departments/${department.id}/users`)} color="info" title={t('departmentList.manageUsers')} aria-label={t('departmentList.manageUsers')}>
                                        <GroupIcon />
                                    </IconButton>
                                    {isInstitutionAdmin && (
                                        <IconButton onClick={() => handleEdit(department)} color="primary" title={t('common:actions.edit')} aria-label={t('common:actions.edit')}>
                                            <EditIcon />
                                        </IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default DepartmentList;
